/* @flow */

import type { $Configuration } from '../types/Configuration';
import type { $Dealer } from '../types/Dealer';
import type { $FilteringState } from '../types/FilteringState';
import FilterType from '../constants/FilterType';
import { ActivityTypesGroupCodes } from '../constants//ActivityTypeGroups';
import { TemplateMarketPropertyKeys } from '../templates/TemplateMarketProperties';
import { filterDealersByActivityTypes } from '../utils/DealerUtils';
import { filterDealersByProductGroups } from '../utils/DealerUtils';
import { getConfigurationMarketProperty } from '../utils/WidgetUtils';

export const ActionTypes = {
  DL_FILTERS_TYPE_LIST_UPDATE: 'dl/filters/SPA_FILTERS_TYPE_LIST_UPDATE',
  DL_FILTERS_RESET_ALL: 'dl/filters/SPA_FILTERS_RESET_ALL'
};

export type $Action =
  | { type: typeof ActionTypes.DL_FILTERS_TYPE_LIST_UPDATE, +filterType: string, +value: any }
  | { type: typeof ActionTypes.DL_FILTERS_RESET_ALL }

export const getFilteredDealersList = (
  dealersListArr: Array<$Dealer>, filters: $FilteringState, configuration: $Configuration, market: string
): Array<$Dealer> => {
  let dealersList = dealersListArr || [];
  if (filters[FilterType.FILTER_ACTIVITY_TYPE]) {
    dealersList = filterDealersByActivityTypes(
      [...dealersList], filters[FilterType.FILTER_ACTIVITY_TYPE], configuration, market);
  }
  if (filters[FilterType.FILTER_PRODUCT_GROUP]) {
    dealersList = filterDealersByProductGroups([...dealersList], filters[FilterType.FILTER_PRODUCT_GROUP]);
  }
  return dealersList;
};

export const modifyFilterCriteria = (filterType: string, value: any) => ({
  type: ActionTypes.DL_FILTERS_TYPE_LIST_UPDATE,
  filterType,
  value
});

export const resetAllFilters = () => ({
  type: ActionTypes.DL_FILTERS_RESET_ALL
});

const initialState: $FilteringState = {};

export default function reducer(state: ?$FilteringState, action: $Action) {
  state = state || initialState;
  switch (action.type) {

    case ActionTypes.DL_FILTERS_TYPE_LIST_UPDATE:
      return {
        ...state,
        [action.filterType]: action.value
      };

    case ActionTypes.DL_FILTERS_RESET_ALL:
      return {
        ...state,
        [FilterType.FILTER_ACTIVITY_TYPE]: [],
        [FilterType.FILTER_PRODUCT_GROUP]: [],
        [FilterType.FILTER_SEARCH_RADIUS]: []
      }

    default:
      return state;
  }
}

export const SEARCH_FILTER_TYPES = {
  BRAND_CODE: 'functions.brandCode',
  PRODUCT_GROUP: 'functions.productGroupCode',
  ACTIVITY_TYPE: 'functions.activityCode',
  ACTIVITY_GROUP: 'functions.activityGroup'
};

export function setDealersSearchFilteringCriteria(
  appliedFilters: Object, configuration: $Configuration, marketCode: string
) {
  const criteria: Array<string> = [];

  const activityTypes = appliedFilters[FilterType.FILTER_ACTIVITY_TYPE];
  if (activityTypes && Array.isArray(activityTypes) && activityTypes.filter(at => at).length === 1) {
    const activityType = activityTypes[0];
    switch (activityType) {

      case ActivityTypesGroupCodes.ACTIVITY_TYPES_GROUP_EQC_SALES: {
        const groupActivities: Array<string> = getConfigurationMarketProperty(
          configuration, marketCode, TemplateMarketPropertyKeys.GEN_ACTIVITY_TYPE_GROUP_EQC_SALES) || [];
        if (groupActivities && Array.isArray(groupActivities) && groupActivities.length > 0)
          criteria.push(`${SEARCH_FILTER_TYPES.ACTIVITY_GROUP}=[${groupActivities.join(',')}]`);
        break;
      }

      case ActivityTypesGroupCodes.ACTIVITY_TYPES_GROUP_EQC_AFTER_SALES: {
        const groupActivities: Array<string> = getConfigurationMarketProperty(
          configuration, marketCode, TemplateMarketPropertyKeys.GEN_ACTIVITY_TYPE_GROUP_EQC_AFTER_SALES) || [];
        if (groupActivities && Array.isArray(groupActivities) && groupActivities.length > 0)
          criteria.push(`${SEARCH_FILTER_TYPES.ACTIVITY_GROUP}=[${groupActivities.join(',')}]`);
        break;
      }

      case ActivityTypesGroupCodes.ACTIVITY_TYPES_GROUP_REPAIR: {
        const groupActivities: Array<string> = getConfigurationMarketProperty(
          configuration, marketCode, TemplateMarketPropertyKeys.GEN_ACTIVITY_TYPE_GROUP_REPAIR) || [];
        if (groupActivities && Array.isArray(groupActivities) && groupActivities.length > 0)
          criteria.push(`${SEARCH_FILTER_TYPES.ACTIVITY_GROUP}=[${groupActivities.join(',')}]`);
        break;
      }

      case ActivityTypesGroupCodes.ACTIVITY_TYPES_GROUP_JUNGE_STERNE: {
        const groupActivities: Array<string> = getConfigurationMarketProperty(
          configuration, marketCode, TemplateMarketPropertyKeys.GEN_ACTIVITY_TYPE_GROUP_JUNGE_STERNE) || [];
        if (groupActivities && Array.isArray(groupActivities) && groupActivities.length > 0)
          criteria.push(`${SEARCH_FILTER_TYPES.ACTIVITY_GROUP}=[${groupActivities.join(',')}]`);
        break;
      }

      case ActivityTypesGroupCodes.ACTIVITY_TYPES_GROUP_COMMERCIAL_SALES: {
        const groupActivities: Array<string> = getConfigurationMarketProperty(
          configuration, marketCode, TemplateMarketPropertyKeys.GEN_ACTIVITY_TYPE_GROUP_COMMERCIAL_SALES) || [];
        if (groupActivities && Array.isArray(groupActivities) && groupActivities.length > 0)
          criteria.push(`${SEARCH_FILTER_TYPES.ACTIVITY_GROUP}=[${groupActivities.join(',')}]`);
        break;
      }

      case ActivityTypesGroupCodes.ACTIVITY_TYPES_GROUP_PRIVATE_SALES: {
        const groupActivities: Array<string> = getConfigurationMarketProperty(
          configuration, marketCode, TemplateMarketPropertyKeys.GEN_ACTIVITY_TYPE_GROUP_PRIVATE_SALES) || [];
        if (groupActivities && Array.isArray(groupActivities) && groupActivities.length > 0)
          criteria.push(`${SEARCH_FILTER_TYPES.ACTIVITY_GROUP}=[${groupActivities.join(',')}]`);
        break;
      }

      default:
        criteria.push(`${SEARCH_FILTER_TYPES.ACTIVITY_TYPE}=${activityType}`);
    }
  }

  const productGroups = appliedFilters[FilterType.FILTER_PRODUCT_GROUP];
  if (productGroups && Array.isArray(productGroups) && productGroups.filter(pg => pg).length === 1) {
    criteria.push(`${SEARCH_FILTER_TYPES.PRODUCT_GROUP}=${productGroups[0]}`);
  }

  return criteria.join(',');
}
